import { useState } from "react";
import { useErrorDisplay } from "../components/ErrorDisplay"
import TemplateClient, { TemplateDisplay, TemplateErrors } from "../apiClients/TemplateClient"
import { useEffect } from "react";
import { useRef } from "react";
import BusySpinner from "../components/BusySpinner";
import { Box, ButtonGroup, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import IconButtonEx from "../components/IconButtonEx";
import { FormatDateTime, IfTrue } from "../Helpers";
import TemplateUpsertPopup from "../popups/TemplateUpsertPopup";
import { Securables, UpsertAction } from "../Enums";
import TemplateEditorPopup from "../popups/TemplateEditorPopup";
import { DataGridPro, GridRowParams, GridToolbar } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import PanelSegment from "../components/PanelSegment";
import { useWindowDimensions } from "../components/WindowDimensions";
import { useSearchBox } from "../components/SearchBox";
import { I18N } from "../I18N";
import TemplateCopyPopup from "../popups/TemplateCopyPopup";
import OnlyForRole from "../components/OnlyForRole";
import { useAccessInfo } from "../contexts/AccessTokenProvider";
import MarketplaceTemplateList from "../marketplace/MarketplaceTemplateList";
import PageTitle from "../components/PageTitle";
import TemplateAiUploadPopup from "../popups/TemplateAiUploadPopup";
import PositionClient from "../apiClients/PositionClient";
import ModernTemplateManagementView, { TemplateManagementDisplay } from "./template-management/ModernTemplateManagementView";
import useViewportMode from "../hooks/useViewportMode";

type TemplateManagementView = 'classic' | 'modern';
const viewPreferenceKey = 'template-management-view';

const TemplateManagementPage = () => {
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [templates, setTemplates] = useState<TemplateDisplay[]>([]);
    const [busy, setBusy] = useState<boolean>(false);
    const initialized = useRef<boolean>();
    const { height } = useWindowDimensions();
    const [SearchBox, searchFilter] = useSearchBox();
    const [positionNames, setPositionNames] = useState<Record<string, string>>({});
    const [view, setView] = useState<TemplateManagementView>(() =>
        window.localStorage.getItem(viewPreferenceKey) === 'classic' ? 'classic' : 'modern');
    const { mode: viewportMode, isDesktop } = useViewportMode();

    const { hasRole } = useAccessInfo();

    const loadData = async () => {
        try {
            setBusy(true);
            const [t, positions] = await Promise.all([
                new TemplateClient().GetAllDisplay(),
                new PositionClient().GetAll().catch(() => []),
            ]);
            setTemplates(t);
            setPositionNames(Object.fromEntries(positions.map(position => [position.Id, position.Title])));

        } catch (error) {
            setError(error, TemplateErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (initialized.current)
            return;

        initialized.current = true;
        loadData();
    });

    const handleDeleteTemplate = async (id: string) => {
        try {
            setBusy(true);
            await new TemplateClient().Delete(id);
            await loadData();

        } catch (error) {
            setError(error, TemplateErrors.Delete);
        }
        finally {
            setBusy(false);
        }
    };

    const handleRenameTemplate = async (id: string, name: string) => {
        try {
            setBusy(true);
            await new TemplateClient().Update(id, name);
            await loadData();
        } catch (error) {
            setError(error, TemplateErrors.Update);
            throw error;
        } finally {
            setBusy(false);
        }
    };

    const detailPaneHeightCalculator = (rows: number): number => {
        if (rows === 1)
            return 100;
        else if (rows === 2)
            return 200;
        else if (rows === 3)
            return 80 * rows;
        else
            return 55 * rows;
    }

    const determineUpsertAction = () => {
        if (hasRole(Securables.template_modify))
            return UpsertAction.Update;

        return UpsertAction.View;
    }

    const getTemplateRevisionDetail = (params: GridRowParams) => {
        if (params.row.TemplateRevisions.length <= 0)
            return null;
        else {
            let heightAdjustment = detailPaneHeightCalculator(params.row.TemplateRevisions.length as number);
            return (
                <div style={{ height: heightAdjustment, width: '100%' }}>
                    <DataGridPro
                        sx={{ backgroundColor: 'divider' }}
                        density="compact"
                        rows={params.row.TemplateRevisions.map(x => { return { ...x, id: x.Id } })}
                        hideFooter={true}
                        columns={[
                            {
                                field: 'Action',
                                sortable: false,
                                filterable: false,
                                resizable: false,
                                width: 100,
                                align: 'left',
                                disableColumnMenu: true,
                                renderCell: ({ row: t }) =>
                                    <ButtonGroup>
                                        {IfTrue(t.IsActive, () =>
                                            <OnlyForRole role={Securables.template}><TemplateEditorPopup mode={UpsertAction.View} templateId={t.TemplateId} templateRevisionId={t.Id} onCreateRevision={() => loadData()} /></OnlyForRole>
                                        )}
                                        {IfTrue(!t.IsActive, () =>
                                            <OnlyForRole role={Securables.template}><TemplateEditorPopup mode={(t.IsDeleted) ? UpsertAction.View : determineUpsertAction()} templateId={t.TemplateId} templateRevisionId={t.Id} onPublish={loadData} /></OnlyForRole>
                                        )}
                                        <OnlyForRole role={Securables.template_create}><TemplateCopyPopup templateId={t.TemplateId} templateName={params.row.Name} onAdd={loadData} /></OnlyForRole>
                                    </ButtonGroup>
                            },
                            {
                                field: 'Revision',
                                headerName: I18N.Translate('Rev'),
                                width: 70
                            },
                            {
                                field: 'IsActive',
                                headerName: I18N.Translate('Active'),
                                flex: 1,
                                renderCell: ({ row: t }) => t.IsActive ? I18N.Translate('Active') : I18N.Translate('Not Active')
                            },
                            {
                                field: 'IsDeleted',
                                headerName: I18N.Translate('Is Deleted'),
                                flex: 1,
                                renderCell: ({ row: t }) => t.IsDeleted ? I18N.Translate('Yes') : I18N.Translate('No')
                            },
                            {
                                field: 'LastUpdateDate',
                                headerName: I18N.Translate('Last Modified'),
                                flex: 1,
                                renderCell: ({ row: t }) => FormatDateTime(dayjs(t.value).toString())
                            },
                            {
                                field: 'Delete',
                                sortable: false,
                                filterable: false,
                                resizable: false,
                                disableColumnMenu: true,
                                width: 70,
                                align: 'center',
                                hide: false,
                                renderCell: ({ row: t }) => IfTrue(!t.IsDeleted && !t.IsActive, () => <IconButtonEx size='small' hoverContent={I18N.Translate('Delete Template')} message={I18N.Translate('Are you sure you want to delete this template')} onClick={() => handleDeleteTemplate(t.Id)}><DeleteIcon htmlColor='red' fontSize='small' /></IconButtonEx>)
                            }
                        ]}
                    />
                </div>

            );
        }
    }

    const handleMarketplaceAdd = async (templateId: string) => {
        await loadData();
    }

    const handleViewChange = (_event, nextView: TemplateManagementView | null) => {
        if (!nextView)
            return;

        setView(nextView);
        window.localStorage.setItem(viewPreferenceKey, nextView);
    };

    const visibleTemplates = searchFilter(templates) as TemplateManagementDisplay[];
    const effectiveView: TemplateManagementView = isDesktop ? view : 'modern';

    return (
        <>
            <ErrorDisplay />
            <BusySpinner busy={busy}>
                <PanelSegment
                    titleContent={<PageTitle title={I18N.Translate("Template Management")} />}
                    toolbar={<Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
                        {isDesktop && <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={view}
                            aria-label="Template management view"
                            onChange={handleViewChange}>
                            <ToggleButton value="classic" aria-label="List template view">
                                <ViewListOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} /> List
                            </ToggleButton>
                            <ToggleButton value="modern" aria-label="Tiles template view">
                                <ViewModuleOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} /> Tiles
                            </ToggleButton>
                        </ToggleButtonGroup>}
                        <ButtonGroup>
                            <OnlyForRole role={Securables.synergist_admin}>
                                <MarketplaceTemplateList onAdd={handleMarketplaceAdd} />
                                <TemplateAiUploadPopup onAdd={loadData} />
                            </OnlyForRole>
                            <OnlyForRole role={Securables.template_create}>
                                <TemplateUpsertPopup mode={UpsertAction.Create} onAdd={() => loadData()} visibleLabel={I18N.Translate('Add Template')} aria-label={I18N.Translate('Add Template')} />
                            </OnlyForRole>
                            <SearchBox />
                        </ButtonGroup>
                    </Stack>}
                    fillHeight>
                    {effectiveView === 'classic' ? <div style={{ height: height - 170, width: '100%' }}>
                        <DataGridPro
                            density="compact"
                            getDetailPanelContent={(row) => getTemplateRevisionDetail(row)}
                            getDetailPanelHeight={(params) => detailPaneHeightCalculator(params.row.TemplateRevisions.length as number)}
                            rows={visibleTemplates.map(x => { return { ...x, id: x.Id } })}
                            columns={[
                                {
                                    field: 'Action',
                                    sortable: false,
                                    filterable: false,
                                    resizable: false,
                                    width: 100,
                                    align: 'center',
                                    disableColumnMenu: true,
                                    renderCell: ({ row: t }) =>
                                        <ButtonGroup>
                                            <OnlyForRole role={Securables.template_modify}><TemplateUpsertPopup mode={UpsertAction.Update} templateId={t.Id} inTemplate={t} onChange={loadData} /></OnlyForRole>
                                        </ButtonGroup>
                                },
                                {
                                    field: 'Name',
                                    headerName: I18N.Translate('Name'),
                                    flex: 1
                                },
                                {
                                    field: 'ActiveStatus',
                                    headerName: I18N.Translate('Status'),
                                    flex: 1
                                },
                                {
                                    field: 'LastUpdateDate',
                                    headerName: I18N.Translate('Last Modified'),
                                    flex: 1,
                                    renderCell: (params) => FormatDateTime(dayjs(params.value).toString())
                                },
                                {
                                    field: 'Delete',
                                    sortable: false,
                                    filterable: false,
                                    resizable: false,
                                    disableColumnMenu: true,
                                    width: 70,
                                    align: 'center',
                                    hide: false,
                                    renderCell: (params) => IfTrue(!params.row.IsDeleted && !params.row.IsActive, () => <IconButtonEx size='small' hoverContent={I18N.Translate('Delete Template')} message={I18N.Translate('Are you sure you want to delete this template')} onClick={() => handleDeleteTemplate(params.row.Id)}><DeleteIcon htmlColor='red' fontSize='small' /></IconButtonEx>)
                                }
                            ]}
                            components={{
                                Toolbar: GridToolbar
                            }}
                        />
                    </div> : <Box
                        data-testid="template-modern-container"
                        data-viewport-mode={viewportMode}
                        sx={isDesktop
                            ? { height: height - 170, overflowY: 'auto' }
                            : { height: 'auto', minHeight: 0, overflow: 'visible' }}>
                        <ModernTemplateManagementView
                            templates={visibleTemplates}
                            positionNames={positionNames}
                            canModifyTemplates={hasRole(Securables.template_modify)}
                            canCreateTemplates={hasRole(Securables.template_create)}
                            onRename={handleRenameTemplate}
                            onDelete={handleDeleteTemplate}
                            onReload={loadData}
                        />
                    </Box>}
                </PanelSegment>
            </BusySpinner>
        </>
    );
}

export default TemplateManagementPage;
